import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import axios from 'axios';
import { API_URL } from '../../config';
import { tokens } from '../../utils/theme';
import { BtnIcon } from '../../components/SharedPageStyles';
import { copyTableAsJSON } from '../../utils/clipboardUtils';

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 20px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    flex-wrap: wrap;
    gap: 12px;
`;

const CategoryTitle = styled.h4`
    font-size: 12px;
    font-weight: 600;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.12em;
`;

const ControlsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const ToggleGroup = styled.div`
    display: flex;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15, 23, 42, 0.1)'};
`;

const ToggleBtn = styled.button`
    background: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15,118,110,0.1)')
        : 'transparent'};
    color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border: none;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: ${props => props.active ? '600' : '500'};

    &:hover {
        background: ${props => !props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.25)' : 'rgba(15,118,110,0.15)')};
        color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#b2dfdb' : '#115e59')
        : (props.theme.palette.mode === 'dark' ? '#fff' : '#000')};
    }
`;

const SetupSummaryRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const OpportunityChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.03)'};
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const FilterChips = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    align-items: center;
`;

const FilterChip = styled.button`
    background: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15,118,110,0.1)')
        : 'transparent'};
    border: 1px solid ${props => props.$active
        ? 'rgba(128, 203, 196, 0.3)'
        : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
    }
`;

const HeatmapWrapper = styled.div`
    overflow-x: auto;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.5)'};
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 14px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
`;

const Th = styled.th`
    padding: 6px 4px;
    font-weight: 500;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
    text-align: center;
    min-width: 60px;
`;

const ThSticky = styled(Th)`
    position: sticky;
    left: 0;
    z-index: 3;
    background: ${props => props.theme.palette.mode === 'dark' ? '#0d1117' : '#f8fafc'};
`;

const Td = styled.td`
    padding: 6px 4px;
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.04);
    background-color: ${props => props.$bg || 'transparent'};
    color: ${props => props.$color || tokens(props.theme.palette.mode).grey[300]};
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.15s;

    &:hover {
        filter: brightness(1.2);
        outline: 1px solid rgba(128, 203, 196, 0.4);
        position: relative;
        z-index: 5;
    }
`;

const RowHeader = styled.td`
    position: sticky;
    left: 0;
    z-index: 2;
    padding: 6px 8px;
    font-weight: 500;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
    background: ${props => props.theme.palette.mode === 'dark' ? '#0d1117' : '#f8fafc'};
    border: 1px solid rgba(255, 255, 255, 0.05);
    text-align: left;
    white-space: nowrap;
`;

const ActiveCellInspector = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.03)'};
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const getCellColors = (val, mode) => {
    if (val === 100) {
        return { bg: 'rgba(255, 255, 255, 0.02)', color: 'rgba(255,255,255,0.25)' };
    }

    if (val >= 60) {
        const alpha = Math.min(0.35, 0.12 + ((val - 60) / 40) * 0.23);
        return { bg: `rgba(38, 166, 154, ${alpha})`, color: '#80cbc4' };
    } else if (val >= 30) {
        return { bg: 'rgba(38, 166, 154, 0.06)', color: '#b2dfdb' };
    } else if (val <= -60) {
        const alpha = Math.min(0.35, 0.12 + ((Math.abs(val) - 60) / 40) * 0.23);
        return { bg: `rgba(239, 83, 80, ${alpha})`, color: '#ef9a9a' };
    } else if (val <= -30) {
        return { bg: 'rgba(239, 83, 80, 0.06)', color: '#ffcdd2' };
    } else {
        return { bg: 'transparent', color: mode === 'dark' ? 'rgba(255,255,255,0.35)' : '#666' };
    }
};

const PERIODS = [
    { key: '1M', label: '1M' },
    { key: '3M', label: '3M' },
    { key: '1Y', label: '1Y' }
];

const CATEGORIES = ["ALL", "Indices", "Commodities", "Forex", "Crypto"];

const AssetCorrelationTab = () => {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('3M');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [selectedCell, setSelectedCell] = useState(null);

    const fetchData = async (selectedPeriod) => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/intermarket/asset-correlation?period=${selectedPeriod}`);
            setData(res.data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch asset correlation matrix:", err);
            setError("Failed to load asset correlation data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(period);
    }, [period]);

    const assetMap = useMemo(() => {
        if (!data || !data.assets) return {};
        const map = {};
        data.assets.forEach(a => {
            map[a.ticker] = a;
        });
        return map;
    }, [data]);

    const filteredTickers = useMemo(() => {
        if (!data || !data.assets) return [];
        if (selectedCategory === 'ALL') {
            return data.assets.map(a => a.ticker);
        }
        return data.assets
            .filter(a => a.category?.toLowerCase() === selectedCategory.toLowerCase())
            .map(a => a.ticker);
    }, [data, selectedCategory]);

    const handleCopyJSON = () => {
        if (!data || !data.matrix) return;
        const formatted = [];
        filteredTickers.forEach(t1 => {
            filteredTickers.forEach(t2 => {
                if (t1 !== t2) {
                    formatted.push({
                        asset1: t1,
                        name1: assetMap[t1]?.name || t1,
                        asset2: t2,
                        name2: assetMap[t2]?.name || t2,
                        correlation: data.matrix[t1]?.[t2] || 0,
                        period
                    });
                }
            });
        });
        copyTableAsJSON(formatted);
    };

    if (loading) {
        return (
            <Container>
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    Fetching asset correlation matrix...
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <div style={{ padding: '20px', color: '#ef5350', background: 'rgba(239, 83, 80, 0.1)', borderRadius: '4px' }}>
                    {error}
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <ScrollableContent>
                <HeaderRow>
                    <CategoryTitle>ASSET CORRELATION</CategoryTitle>

                    <ControlsRow>
                        <ToggleGroup>
                            {PERIODS.map(p => (
                                <ToggleBtn
                                    key={p.key}
                                    active={period === p.key}
                                    onClick={() => setPeriod(p.key)}
                                >
                                    {p.label}
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>

                        <BtnIcon onClick={handleCopyJSON} title="Copy JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                    </ControlsRow>
                </HeaderRow>

                {/* Top setups: sober, concise pill row matching Market Overview */}
                {data && (
                    <SetupSummaryRow>
                        {data.topPositive?.slice(0, 3).map((item, idx) => (
                            <OpportunityChip key={`pos-${idx}`}>
                                <span style={{ fontWeight: 500 }}>{item.asset1} / {item.asset2}</span>
                                <span style={{
                                    color: tokens(theme.palette.mode).greenAccent[500],
                                    fontWeight: 500,
                                    fontSize: '11px'
                                }}>
                                    +{item.correlation}%
                                </span>
                            </OpportunityChip>
                        ))}
                        {data.topNegative?.slice(0, 3).map((item, idx) => (
                            <OpportunityChip key={`neg-${idx}`}>
                                <span style={{ fontWeight: 500 }}>{item.asset1} / {item.asset2}</span>
                                <span style={{
                                    color: tokens(theme.palette.mode).redAccent[500],
                                    fontWeight: 500,
                                    fontSize: '11px'
                                }}>
                                    {item.correlation}%
                                </span>
                            </OpportunityChip>
                        ))}
                    </SetupSummaryRow>
                )}

                {/* Filter row */}
                <FilterRow>
                    <CategoryTitle style={{ fontSize: '11px', margin: 0 }}>
                        FILTER
                    </CategoryTitle>
                    <FilterChips>
                        {CATEGORIES.map(cat => (
                            <FilterChip
                                key={cat}
                                $active={selectedCategory === cat}
                                onClick={() => setSelectedCategory(cat)}
                            >
                                {cat}
                            </FilterChip>
                        ))}
                    </FilterChips>
                </FilterRow>

                {/* Matrix Heatmap */}
                <HeatmapWrapper>
                    <Table>
                        <thead>
                            <tr>
                                <ThSticky style={{ textAlign: 'left', minWidth: '75px' }}>ASSET</ThSticky>
                                {filteredTickers.map(ticker => (
                                    <Th key={ticker} title={assetMap[ticker]?.name || ticker}>
                                        {ticker}
                                    </Th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickers.map(t1 => (
                                <tr key={t1}>
                                    <RowHeader title={assetMap[t1]?.name || t1}>
                                        {t1}
                                    </RowHeader>
                                    {filteredTickers.map(t2 => {
                                        const val = data?.matrix?.[t1]?.[t2] ?? 0;
                                        const { bg, color } = getCellColors(val, theme.palette.mode);
                                        return (
                                            <Td
                                                key={t2}
                                                $bg={bg}
                                                $color={color}
                                                onClick={() => setSelectedCell({ t1, t2, val })}
                                                title={`${t1} vs ${t2}: ${val > 0 ? `+${val}` : val}%`}
                                            >
                                                {val > 0 ? `+${val}` : val}
                                            </Td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </HeatmapWrapper>

                {/* Inspector Banner */}
                {selectedCell && (
                    <ActiveCellInspector>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'
                            }}>
                                {selectedCell.t1} / {selectedCell.t2}
                            </span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: selectedCell.val >= 60
                                    ? tokens(theme.palette.mode).greenAccent[500]
                                    : (selectedCell.val <= -60 ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).grey[300])
                            }}>
                                {selectedCell.val > 0 ? `+${selectedCell.val}%` : `${selectedCell.val}%`}
                            </span>
                        </div>

                        <div style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400] }}>
                            {selectedCell.val >= 60 && "Forte corrélation (risque d'exposition cumulée)"}
                            {selectedCell.val <= -60 && "Corrélation inverse (effet de couverture naturelle)"}
                            {selectedCell.val > -60 && selectedCell.val < 60 && "Corrélation modérée ou neutre (diversification)"}
                        </div>
                    </ActiveCellInspector>
                )}
            </ScrollableContent>
        </Container>
    );
};

export default AssetCorrelationTab;
